import React, { useRef } from "react";
import Fetching from "../../Fetching";
import DiItems from "../../DisplayItems/DiItems";
import EditorWrapper from "../../Editor/EditorWrapper";
import { useAgent } from "./hooks/useAgent";
import { useAgentPage } from "./hooks/useAgentPage";
import AgentWrapper from "./AgentWrapper";
import MicroContent from "../../MicroContent";
import AgentStickyBar from "./AgentStickyBar";
import ListingsMap from "../../ListingsMap";
import AgentHero from "./AgentHero";
import AgentTeams from "./AgentTeams";
import Contact from "../../Contact";

/**
 * The Agent page component.
 * @returns The content and data for the Agent page.
 */
const Agent = () => {
    useAgentPage();
    const {
        isLoading,
        agent_pic,
        agentName,
        agentId,
        agentMedia,
        agent_description,
        modifiedAgentAreas,
        handleAgentAreaClick,
        internalAgentID,
        microContent,
        publishAgentContent,
        agentListings,
        teamInfo,
        hasTeam,
        isTeamMember,
        agentAdjective,
    } = useAgent();
    const headerRef = useRef(null);

    return (
        <Fetching waitUntil={!isLoading}>
            <div data-testid="agent-main">
                <div id="agent-hero-header" ref={headerRef}>
                    <AgentHero
                        agentName={agentName}
                        agentMedia={agentMedia}
                        agentImage={agent_pic}
                    />
                </div>

                <AgentStickyBar headerRef={headerRef} />

                <AgentWrapper>
                    <div className="row g-0">
                        <div
                            className="col g-0 full-bio"
                            data-testid="agent-long-bio"
                            id="About"
                        >
                            <EditorWrapper
                                allowTitleEdit
                                defaultTitleText={`About ${agentName}`}
                                category="AGENTS"
                                item="Long-Bio"
                                id={agentId}
                                editorName="RichTextEditor"
                                defaultContent={
                                    microContent?.["Long-Bio"] ||
                                    agent_description?.value
                                }
                                publishCallback={publishAgentContent}
                            >
                                {({ injectedContent }) => (
                                    <MicroContent
                                        defaultContent={
                                            injectedContent ||
                                            agent_description?.value
                                        }
                                        defaultTitleText={`About ${agentName}`}
                                        microContent={injectedContent}
                                        className="d-inline-block me-4 w-100"
                                    />
                                )}
                            </EditorWrapper>
                        </div>
                    </div>
                </AgentWrapper>

                <AgentWrapper>
                    <div id="Media">
                        <EditorWrapper
                            allowTitleEdit
                            defaultTitleText="Media"
                            category="AGENTS"
                            item="Media"
                            id={agentId}
                            editorName="RichTextEditor"
                            defaultContent={microContent?.["Media"]}
                            publishCallback={publishAgentContent}
                        >
                            {({ injectedContent }) => (
                                <MicroContent
                                    defaultTitleText="Media"
                                    defaultContent={
                                        injectedContent ||
                                        microContent?.["Media"]
                                    }
                                    microContent={injectedContent}
                                    className="d-inline-block me-4 w-100"
                                />
                            )}
                        </EditorWrapper>
                    </div>
                </AgentWrapper>

                {!isTeamMember && hasTeam && (
                    <AgentWrapper
                        title={`Meet ${agentAdjective} Team`}
                        className="pt-3 pb-4"
                        backgroundColor="var(--bs-gray-200)"
                    >
                        <AgentTeams teamInfo={teamInfo} />
                    </AgentWrapper>
                )}

                <AgentWrapper
                    className="pb-3"
                    title={`${agentAdjective} Service Areas`}
                >
                    <div className="mb-3">
                        Visit each service area to see all listings available in
                        each market.
                    </div>

                    {modifiedAgentAreas &&
                        (modifiedAgentAreas || []).map(
                            ({ children, value }) => (
                                <div
                                    className="mb-2"
                                    key={`service-area-${value}`}
                                >
                                    <div>{value}</div>

                                    <div
                                        style={{ columnWidth: "290px" }}
                                        className="ms-2"
                                    >
                                        <DiItems
                                            wrapperClsses="area-served"
                                            items={children}
                                            excludeIdsFromState={["agentId"]}
                                            valueLinkClasses="pl-3"
                                            clickHandler={handleAgentAreaClick}
                                            linkQueryParams={`?lhra_id=${internalAgentID}`}
                                            dataTest="area-served"
                                        />
                                    </div>
                                </div>
                            )
                        )}
                </AgentWrapper>

                {agentListings && (
                    <AgentWrapper
                        className="pb-3"
                        title={`${agentAdjective} Current Listings`}
                    >
                        <div>
                            <ListingsMap listings={agentListings} />
                        </div>
                    </AgentWrapper>
                )}

                <AgentWrapper>
                    <div id="Testimonials">
                        <EditorWrapper
                            allowTitleEdit
                            defaultTitleText="Client Testimonials"
                            category="AGENTS"
                            item="Testimonials"
                            id={agentId}
                            editorName="RichTextEditor"
                            defaultContent={microContent?.["Testimonials"]}
                            publishCallback={publishAgentContent}
                        >
                            {({ injectedContent }) => (
                                <MicroContent
                                    defaultTitleText="Client Testimonials"
                                    defaultContent={
                                        injectedContent ||
                                        microContent?.["Testimonials"]
                                    }
                                    microContent={injectedContent}
                                    className="d-inline-block me-4 w-100"
                                />
                            )}
                        </EditorWrapper>
                    </div>
                </AgentWrapper>

                <AgentWrapper
                    backgroundColor="var(--bs-gray-200)"
                    className="py-4 d-flex flex-column align-items-center px-3 px-md-0"
                >
                    <div className="fw-semibold fs-2 mb-4">{`Contact ${agentName}`}</div>

                    <div
                        className="bg-white p-3"
                        style={{ maxWidth: "500px" }}
                        data-test="agent-contact-form"
                    >
                        <Contact agentId={internalAgentID} />
                    </div>
                </AgentWrapper>

                <AgentWrapper>
                    <div id="Files">
                        <EditorWrapper
                            allowTitleEdit
                            defaultTitleText="Files"
                            category="AGENTS"
                            item="Files"
                            id={agentId}
                            editorName="RichTextEditor"
                            defaultContent={microContent?.["Files"]}
                            publishCallback={publishAgentContent}
                        >
                            {({ injectedContent }) => (
                                <MicroContent
                                    defaultTitleText="Files"
                                    defaultContent={
                                        injectedContent ||
                                        microContent?.["Files"]
                                    }
                                    microContent={injectedContent}
                                    className="d-inline-block me-4 w-100"
                                />
                            )}
                        </EditorWrapper>
                    </div>
                </AgentWrapper>
            </div>
        </Fetching>
    );
};

export default Agent;
